import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { errorMessages } from '~/constants/errorMessages.ts';
import { safeWindowOpen } from '~/helpers/safeUrlOpen.ts';
import { withBlobContentType } from '~/helpers/utils.ts';
import {
  deleteGuaranteesClientDocument,
  downloadGuaranteesClientDocument,
  fetchGuaranteesApplicationFiles,
  uploadGuaranteesClientDocument,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  GuaranteesApplicationRouteId,
  GuaranteesFileType,
  type TGuaranteesApplicationFile,
} from '~/store/guarantees/types.ts';
import type { AppDispatch } from '~/store/store.ts';
import { UDBTF_GUARANTEE_SCAN_COPY_TEXT } from './constants.ts';
import { mapUdbtfGuaranteeScanCopyFile } from './utils.ts';
import {
  notifyGuaranteesApplicationFilesChanged,
  useGuaranteesApplicationFilesSync,
} from '../shared/SidePanel/applicationFilesSync.ts';

type TUseUdbtfGuaranteeScanCopyDocumentsParams = {
  applicationId: string;
  effectiveApplicationId: string;
  setModalAlert: (value: { text: string }) => void;
  showGuaranteesRequestError: (error: unknown) => void;
};

export const useUdbtfGuaranteeScanCopyDocuments = ({
  applicationId,
  effectiveApplicationId,
  setModalAlert,
  showGuaranteesRequestError,
}: TUseUdbtfGuaranteeScanCopyDocumentsParams) => {
  const dispatch = useDispatch<AppDispatch>();
  const [applicationFiles, setApplicationFiles] = useState<TGuaranteesApplicationFile[]>([]);
  const [isApplicationFilesLoading, setIsApplicationFilesLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [hasApplicationFilesLoaded, setHasApplicationFilesLoaded] = useState(false);
  const [uploadingDocumentId, setUploadingDocumentId] = useState<string | null>(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const initialApplicationFilesRequestIdRef = useRef<string | null>(null);
  const scanCopyDocument = useMemo(
    () => mapUdbtfGuaranteeScanCopyFile(applicationFiles),
    [applicationFiles]
  );

  const replacePreviewUrl = useCallback((nextPreviewUrl: string) => {
    setPreviewUrl((currentPreviewUrl) => {
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
      }
      return nextPreviewUrl;
    });
  }, []);

  const refreshApplicationFiles = useCallback(async () => {
    if (!applicationId || applicationId === GuaranteesApplicationRouteId.New) {
      setApplicationFiles([]);
      setHasApplicationFilesLoaded(true);
      replacePreviewUrl('');
      return [];
    }

    setIsApplicationFilesLoading(true);

    try {
      const nextApplicationFiles = await dispatch(
        fetchGuaranteesApplicationFiles({ applicationId })
      ).unwrap();

      setApplicationFiles(nextApplicationFiles);
      setHasApplicationFilesLoaded(true);
      return nextApplicationFiles;
    } finally {
      setIsApplicationFilesLoading(false);
    }
  }, [applicationId, dispatch, replacePreviewUrl]);

  useGuaranteesApplicationFilesSync(effectiveApplicationId, () => {
    void refreshApplicationFiles().catch(showGuaranteesRequestError);
  });

  const loadPreviewDocument = useCallback(
    async (document = scanCopyDocument) => {
      if (!document?.fileId) {
        replacePreviewUrl('');
        return;
      }

      setIsPreviewLoading(true);

      try {
        const downloadedDocument = await dispatch(
          downloadGuaranteesClientDocument({
            id: document.fileId,
            isAttached: true,
          })
        ).unwrap();
        const previewBlob = withBlobContentType(
          downloadedDocument.data,
          downloadedDocument.contentType
        );

        replacePreviewUrl(URL.createObjectURL(previewBlob));
      } catch (error) {
        replacePreviewUrl('');
        showGuaranteesRequestError(error);
      } finally {
        setIsPreviewLoading(false);
      }
    },
    [dispatch, replacePreviewUrl, scanCopyDocument, showGuaranteesRequestError]
  );

  const handleAddDocument = async (file?: File | null) => {
    if (!file || scanCopyDocument) {
      return;
    }

    if (!effectiveApplicationId) {
      showGuaranteesRequestError({ message: errorMessages.SERVICE_UNAVAILABLE });
      return;
    }

    const documentId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    setUploadingDocumentId(documentId);

    try {
      const uploadedDocument = await dispatch(
        uploadGuaranteesClientDocument({
          applicationId: effectiveApplicationId,
          fileType: GuaranteesFileType.GUARANTEE_SCAN_COPY,
          multipartFile: file,
        })
      ).unwrap();
      const nextApplicationFiles = await refreshApplicationFiles();
      const uploadedScanCopyFile: TGuaranteesApplicationFile = {
        id: uploadedDocument.id,
        fileId: uploadedDocument.id,
        description: uploadedDocument.fileName,
        fileType: GuaranteesFileType.GUARANTEE_SCAN_COPY,
      };
      const nextScanCopyDocument = mapUdbtfGuaranteeScanCopyFile(nextApplicationFiles);
      const effectiveScanCopyDocument =
        nextScanCopyDocument ?? mapUdbtfGuaranteeScanCopyFile([uploadedScanCopyFile]);

      if (!nextScanCopyDocument) {
        setApplicationFiles([...nextApplicationFiles, uploadedScanCopyFile]);
      }

      await loadPreviewDocument(effectiveScanCopyDocument);
      notifyGuaranteesApplicationFilesChanged(effectiveApplicationId);
    } catch (error) {
      showGuaranteesRequestError(error);
    } finally {
      setUploadingDocumentId(null);
    }
  };

  const handleOpenDocument = () => {
    if (!previewUrl) {
      setModalAlert({ text: UDBTF_GUARANTEE_SCAN_COPY_TEXT.missingFileMessage });
      return;
    }

    safeWindowOpen(previewUrl, { base: window.location.origin });
  };

  const handleDeleteUploadedDocument = async () => {
    const applicationFileId = scanCopyDocument?.applicationFileId;

    if (!applicationFileId) {
      setModalAlert({ text: UDBTF_GUARANTEE_SCAN_COPY_TEXT.missingFileMessage });
      return;
    }

    setDeletingDocumentId(scanCopyDocument.id);

    try {
      await dispatch(deleteGuaranteesClientDocument({ applicationFileId })).unwrap();
      await refreshApplicationFiles();
      replacePreviewUrl('');
      notifyGuaranteesApplicationFilesChanged(effectiveApplicationId);
    } catch (error) {
      showGuaranteesRequestError(error);
    } finally {
      setDeletingDocumentId(null);
    }
  };

  useEffect(() => {
    if (!applicationId || applicationId === GuaranteesApplicationRouteId.New) {
      initialApplicationFilesRequestIdRef.current = null;
      void refreshApplicationFiles().catch(showGuaranteesRequestError);
      return;
    }

    if (initialApplicationFilesRequestIdRef.current === applicationId) {
      return;
    }

    initialApplicationFilesRequestIdRef.current = applicationId;

    void refreshApplicationFiles().catch((error) => {
      initialApplicationFilesRequestIdRef.current = null;
      showGuaranteesRequestError(error);
    });
  }, [applicationId, refreshApplicationFiles, showGuaranteesRequestError]);

  useEffect(() => {
    if (!hasApplicationFilesLoaded) {
      return;
    }

    void loadPreviewDocument();
  }, [hasApplicationFilesLoaded, loadPreviewDocument]);

  useEffect(
    () => () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl]
  );

  return {
    deletingDocumentId,
    handleAddDocument,
    handleDeleteUploadedDocument,
    handleOpenDocument,
    hasApplicationFilesLoaded,
    isApplicationFilesLoading,
    isPreviewLoading,
    previewUrl,
    uploadingDocumentId,
  };
};
